import React, { useEffect, useState } from 'react'
import {
  Container,
  Checkbox,
  TextCaption,
  TextTitle,
  TextBody,
  Icon,
  FieldPassword,
  Button,
  State,
  RadioGroup,
  BlockingLoader
} from '@pmi/pmi-iqos-ds-react'
import {
  ShippingTypes,
  DeliveryDateMode,
  htmlTranslation,
  registerForm,
  unregisterForm,
  analytics,
  Sites,
  SHIPPING_TK_MAP
} from '@pmi/pmi-iqos-shared-react'
import { ZynAnalytics } from '@zyn-common/services'
import { CheckoutDelivery } from '@zyn-common/components'

const CheckoutStep2 = ({
  translations,
  products,
  shippingMethods,
  selectedShippingMethodIndex,
  handleSelectShippingMethod,
  pickupFailed,
  selectedPickupStore,
  preorder,
  enableNextStep,
  handleDeliveryOptionsUpdate,
  deliveryOptions,
  loading,
  loadingStep2,
  isSagawa,
  isMonitorPack,
  isPurchaseEnabled,
  hasTobacco,
  tobaccoPassword,
  setMainStateProperty,
  purchase,
  termsAndConds,
  handleCheckboxChange,
  isOpen
}) => {
  const [dateMode, setDateMode] = useState(DeliveryDateMode.NO_DATE)
  const [currentDelivery, setCurrentDelivery] = useState(null)
  const [currentDeliveryTimeSlot, setCurrentDeliveryTimeSlot] = useState(null)
  const [analyticsFiredStepTwo, setAnalyticsFiredStepTwo] = useState(false)

  const formId = registerForm()

  useEffect(() => {
    return () => {
      unregisterForm(formId)
    }
  }, [])

  useEffect(() => {
    if (isOpen && !analyticsFiredStepTwo) {
      analytics.virtualPageView('shipping information', 'checkout')
      analytics.checkoutView(products, 2, 'default', null, Sites.zyn)
      setAnalyticsFiredStepTwo(true)
    }
  }, [isOpen])

  const updateDeliveryOption = (
    currentDelivery,
    currentDeliveryTimeSlot,
    dateMode
  ) => {
    setDateMode(dateMode)
    setCurrentDelivery(currentDelivery)
    setCurrentDeliveryTimeSlot(currentDeliveryTimeSlot)

    handleDeliveryOptionsUpdate({
      dayId:
        currentDelivery && currentDelivery.day && currentDelivery.day.leadDays,
      ...currentDeliveryTimeSlot
    })
    analytics.checkoutOption(
      2,
      (currentDeliveryTimeSlot && currentDeliveryTimeSlot.label) || 'default'
    )
  }

  const handleChangeMode = e => {
    const dateMode = e.target.value
    const defaultDelivery =
      deliveryOptions && dateMode === DeliveryDateMode.DATE_SELECTION
        ? deliveryOptions.length > 0
          ? deliveryOptions[0]
          : null
        : null
    updateDeliveryOption(
      defaultDelivery,
      defaultDelivery &&
        defaultDelivery.timeSlots &&
        defaultDelivery.timeSlots.length > 0
        ? defaultDelivery.timeSlots[0]
        : null,
      dateMode
    )
  }

  const handleChangeDate = e => {
    const date = e.target.value
    if (date instanceof Date && !isNaN(date)) {
      const filteredDeliveries =
        deliveryOptions.filter(
          delivery =>
            (delivery.day ? new Date(delivery.day.date).getTime() : null) ===
            date.getTime()
        ) || []
      const currentDelivery =
        filteredDeliveries.length > 0 ? filteredDeliveries[0] : null
      updateDeliveryOption(
        currentDelivery,
        currentDelivery &&
          currentDelivery.timeSlots &&
          currentDelivery.timeSlots.length > 0
          ? currentDelivery.timeSlots[0]
          : null,
        dateMode
      )
    }
  }

  const handleChangeHour = e => {
    const deliveryHour = parseInt(e.target.value)
    const filteredDeliveryTimeSlots =
      (currentDelivery &&
        currentDelivery.timeSlots &&
        currentDelivery.timeSlots.filter(
          timeSlot => timeSlot.hour === deliveryHour
        )) ||
      []
    const currentDeliveryTimeSlot =
      filteredDeliveryTimeSlots.length > 0 ? filteredDeliveryTimeSlots[0] : null
    updateDeliveryOption(currentDelivery, currentDeliveryTimeSlot, dateMode)
  }

  const handleShippingMethodChange = e => {
    const index = parseInt(e.target.value || '')
    if (selectedShippingMethodIndex !== index) {
      handleSelectShippingMethod && handleSelectShippingMethod(index)
    }
  }

  const isDeliverySetupCompleted = () => {
    // Pickup
    const shippingType = shippingMethods[selectedShippingMethodIndex].shippingId
    if (
      shippingType === ShippingTypes.FSS ||
      shippingType === ShippingTypes.FSS_SIS_LOCKER ||
      shippingType === ShippingTypes.CSTORE
    ) {
      return selectedPickupStore !== undefined && selectedPickupStore !== null
    }

    const hasDates =
      deliveryOptions &&
      deliveryOptions.length > 0 &&
      (
        deliveryOptions.filter(
          delivery => delivery.day && !!delivery.day.date
        ) || []
      ).length > 0

    return (
      dateMode === DeliveryDateMode.NO_DATE ||
      (dateMode === DeliveryDateMode.DATE_SELECTION &&
        currentDelivery &&
        (!hasDates ||
          (currentDelivery.day &&
            (currentDelivery.day.leadDays ||
              currentDelivery.day.leadDays === 0))) &&
        currentDeliveryTimeSlot &&
        (currentDeliveryTimeSlot.hour || currentDeliveryTimeSlot.hour === 0))
    )
  }

  const handleConfirmAddress = () => {
    ZynAnalytics.deliveryTimeClick()
    enableNextStep(1)
  }

  const renderCheckoutDelivery = () => {
    const hasDates =
      deliveryOptions &&
      deliveryOptions.length > 0 &&
      (
        deliveryOptions.filter(
          delivery => delivery.day && !!delivery.day.date
        ) || []
      ).length > 0

    return (
      <>
        <BlockingLoader loading={loadingStep2} />
        <Container>
          <Container pb={['6', '4']}>
            <TextTitle heading={TextTitle.headings.h6}>
              {translations.tk_checkoutStepTitleDelivery}
            </TextTitle>
          </Container>
          {!hasDates && (
            <Container pb={['6', '4']}>
              <TextBody
                size={TextBody.sizes.m}
                {...htmlTranslation(translations, 'tk_checkoutIMSDisclamer')}
              />
            </Container>
          )}
          <TextBody
            size={TextBody.sizes.m}
            {...htmlTranslation(
              translations,
              'tk_checkoutStepSubtitleDelivery'
            )}
          />
          <Container pt="8">
            <CheckoutDelivery
              dateMode={dateMode}
              deliveryOptions={deliveryOptions}
              currentDelivery={currentDelivery}
              currentDeliveryTimeSlot={currentDeliveryTimeSlot}
              handleChangeMode={handleChangeMode}
              handleChangeDate={handleChangeDate}
              handleChangeHour={handleChangeHour}
              translations={translations}
              preorder={preorder}
            />
          </Container>
        </Container>
      </>
    )
  }

  const selectedShippingMethod = shippingMethods[selectedShippingMethodIndex]

  const shippingMethodsItems = (() => {
    return shippingMethods && shippingMethods.length > 0
      ? shippingMethods
          .filter(el => el.shippingId === ShippingTypes.YAMATO)
          .map((el, index) => ({
            value: index,
            label: (
              <Container
                display={Container.displays.flex}
                alignItems={Container.alignItemsValues.center}
                height="10">
                <TextCaption size={TextCaption.sizes.m}>
                  {translations[
                    'tk_shippingType' + SHIPPING_TK_MAP[el.shippingId]
                  ] || el.shipping}
                </TextCaption>
              </Container>
            ),
            content: (
              <Container fullWidth>
                <TextBody
                  size={TextBody.sizes.m}
                  {...htmlTranslation(
                    translations,
                    'tk_shippingDetailsType' +
                      SHIPPING_TK_MAP[selectedShippingMethod.shippingId]
                  )}
                />
                {el.shippingId === ShippingTypes.YAMATO
                  ? renderCheckoutDelivery()
                  : null}
              </Container>
            ),
            selected: index === selectedShippingMethodIndex,
            disabled:
              (el.shippingId === ShippingTypes.FSS_SIS_LOCKER ||
                el.shippingId === ShippingTypes.CSTORE ||
                el.shippingId === ShippingTypes.FSS) &&
              pickupFailed
          }))
      : []
  })()

  const handleTobaccoPasswordChange = e => {
    setMainStateProperty(e.target.name, e.target.value)
  }

  const renderPaymentMethodBottomArea = () => {
    return (
      <Container bg={Container.backgrounds.hardWhite}>
        <Container pt="4" />
        <Container py="6" bg={Container.backgrounds.white}>
          {hasTobacco && (
            <Container px={['4', '6']} pb="6" flexWrap>
              <Container
                display={Container.displays.flex}
                justifyContent={Container.justifyContentValues.center}
                alignItems={Container.alignItemsValues.center}
                flexDirection={Container.flexDirections.column}>
                <TextCaption
                  alignment={TextCaption.alignments.center}
                  size={TextCaption.sizes.m}>
                  {translations.tk_password}
                </TextCaption>
                <Container>
                  <FieldPassword
                    value={tobaccoPassword}
                    name="tobaccoPassword"
                    onChangeHandler={handleTobaccoPasswordChange}
                    noAutoComplete
                    data-testing="Checkout_FieldPassword_TobaccoPassword"
                  />
                </Container>
              </Container>
            </Container>
          )}

          <Container pb="6" px={['4', '6']}>
            <Container pt="6" display={Container.displays.flex}>
              <Checkbox
                fullWidth
                checked={termsAndConds.toggleMonitorPackTermsAndCond}
                name="toggleMonitorPackTermsAndCond"
                template={
                  <TextBody
                    {...htmlTranslation(
                      translations,
                      'tk_stepTwoMonitorPackTermsAndCondCheckbox'
                    )}
                  />
                }
                onChangeHandler={() =>
                  handleCheckboxChange('toggleMonitorPackTermsAndCond')
                }
                data-testing="Checkout_Checkbox_MonitorPackTerms"
              />
            </Container>
            <Container pt="6">
              <TextBody
                size={TextBody.sizes.s}
                {...htmlTranslation(translations, 'tk_zeroYenBeforeConfirm')}
              />
            </Container>
          </Container>

          <Container
            display={Container.displays.flex}
            justifyContent={Container.justifyContentValues.center}>
            <Button
              onClickHandler={purchase}
              disabled={!isPurchaseEnabled}
              text={translations.tk_stepThreeConfirmOrder}
              data-testing="Checkout_Button_ConfirmOrder"
            />
          </Container>
        </Container>
        <Container pt="8" px={['4', '0']}>
          <TextBody
            style={TextBody.styles.grayedout}
            size={TextBody.sizes.s}
            data-testing="Checkout_TextBody_PaymentType"
            {...htmlTranslation(translations, 'tk_zeroYenLawReminder')}
          />
        </Container>
      </Container>
    )
  }

  return (
    <>
      <Container p={['4', '6']}>
        {shippingMethods.length > 1 ? (
          <Container>
            <Container pb="2">
              <TextBody
                size={TextBody.sizes.m}
                {...htmlTranslation(
                  translations,
                  'tk_checkoutStepSubtitleShippingMethod'
                )}
              />
            </Container>
            <RadioGroup
              name="shippingMethods"
              label=""
              style={RadioGroup.styles.border}
              columns={1}
              items={shippingMethodsItems}
              getItemLabel={item => item.label}
              getItemValue={item => item.value}
              getItemContent={item => item.content}
              isItemSelected={item => item.selected}
              onChangeHandler={handleShippingMethodChange}
              showOnlySelectedContent
              spacing={RadioGroup.spacingValues.large}
              data-testing="Checkout_RadioGroup_ShippingMethods"
            />
          </Container>
        ) : (
          <Container fullWidth px="4">
            {selectedShippingMethod.shippingId === ShippingTypes.YAMATO
              ? renderCheckoutDelivery()
              : null}
          </Container>
        )}
        {isSagawa && (
          <Container pt="4">
            <Container
              bg={Container.backgrounds.hardWhite}
              display={Container.displays.flex}
              p="8">
              <Container pr="4" pt="2">
                <State state={State.states.warning} size={State.sizes.xs} />
              </Container>
              <Container>{translations.tk_stepOneNoticeBlock}</Container>
            </Container>
          </Container>
        )}
        {!isMonitorPack ? (
          <Container
            fullWidth
            display={Container.displays.flex}
            alignItems={Container.alignItemsValues.center}
            justifyContent={Container.justifyContentValues.center}
            pt="8"
            pb={['2', '0']}>
            <Button
              text={translations.tk_stepOneSaveButton}
              disabled={!isDeliverySetupCompleted() || loading}
              onClickHandler={handleConfirmAddress}
              variant={Button.variants.primary}
              icon={Icon.images.arrowDown}
              iconPosition={Button.iconPositions.left}
              data-testing="Checkout_Button_SaveStepOne"
            />
          </Container>
        ) : null}
      </Container>
      {isMonitorPack ? renderPaymentMethodBottomArea() : null}
    </>
  )
}

export default CheckoutStep2
